import { useState, useEffect, useCallback } from 'react'
import { message } from 'antd'
import { countInvoices, generateCraftMyPdfReport, getInvoices, invoicesAggregate, updateInvoice } from '../../../utils'
import { readPatients } from '../../../../Admin/patients/utils/utils'
import { handleError } from '../../../../lib/utils'
import { goodXStatus } from '../../../../Bookings/utils/utils'
import dayjs from 'dayjs'
import InvoiceTemplate from '../components/InvoiceTemplate'
import store from '../../../../../store'
import { useSelector } from 'react-redux'
import {
  logAuditEvent,
  AuditActions,
  AuditEntityTypes
} from '../../../../Billing-Module-Enhanced/backend-integration/api/auditTrail'

/**
 * Custom hook for managing invoices list operations
 */
export const useInvoices = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)
  const [filters, setFilters] = useState({
    status: undefined,
    invoiceType: undefined,
    createdAt: undefined,
    search: undefined
  })
  const [printingInvoiceId, setPrintingInvoiceId] = useState(null)

  /**
   * Fetches invoices based on current filters and pagination
   */
  const fetchInvoices = async () => {
    let tmpFilters = { ...filters }

    setLoading(true)

    try {
      // Process filters more efficiently
      Object.keys(tmpFilters).forEach((key) => {
        if (!tmpFilters[key]) {
          delete tmpFilters[key]
          return
        }

        if (key === 'createdAt') {
          const date = new Date(tmpFilters[key])
          tmpFilters[key] = {
            $lte: dayjs(date).set('hour', 23).set('minute', 59).set('second', 59).toDate(),
            $gte: dayjs(date).set('hour', 0).set('minute', 0).set('second', 0).toDate()
          }
        }

        if (key === 'search') {
          tmpFilters.$or = [
            { invoiceNo: { $regex: tmpFilters[key], $options: 'i' } },
            { claimStatus: { $regex: tmpFilters[key], $options: 'i' } },
            { status: { $regex: tmpFilters[key], $options: 'i' } }
          ]
          delete tmpFilters[key]
        }
      })

      // Get total count and invoice data in parallel
      const [tmpTotal, tmpData] = await Promise.all([
        countInvoices(tmpFilters),
        getInvoices(tmpFilters, page, pageSize)
      ])

      // Extract the count value from the response
      const totalCount = typeof tmpTotal === 'object' ? tmpTotal.result || tmpTotal.count || 0 : tmpTotal
      setTotal(totalCount)

      // Collect all IDs in a single pass
      const patientIds = new Set()
      const bookingIds = new Set()
      const clinicIds = new Set()
      const medicalProfIds = new Set()
      const goodXTicketNos = []

      tmpData.forEach((entry) => {
        patientIds.add(entry.userRef)

        if (entry.medicalProfRef) medicalProfIds.add(entry.medicalProfRef)
        if (entry.refMedProfRef) medicalProfIds.add(entry.refMedProfRef)
        if (entry.assMedProfRef) medicalProfIds.add(entry.assMedProfRef)

        if (entry.bookingRef) bookingIds.add(entry.bookingRef)
        if (entry.clinicRef) clinicIds.add(entry.clinicRef)

        if (entry.goodXTicketNo)
          goodXTicketNos.push({
            index: tmpData.indexOf(entry),
            ticketNo: entry.goodXTicketNo
          })
      })

      // Process GoodX data in parallel if needed
      if (goodXTicketNos.length > 0) {
        // Create all promises at once
        const statusPromises = goodXTicketNos.map(async ({ index, ticketNo }) => {
          const status = await goodXStatus(ticketNo)
          return {
            index,
            goodXStatus: status,
            claimResponse: null,
            claimStatus: 'N/A'
          }
        })

        // Execute all promises in parallel
        const results = await Promise.all(statusPromises)

        // Apply GoodX results to tmpData
        results.forEach((result) => {
          Object.assign(tmpData[result.index], {
            goodXStatus: result.goodXStatus,
            claimResponse: result.claimResponse,
            claimStatus: result.claimStatus
          })
        })
      }

      // Set default GoodX values for entries without goodXTicketNo
      tmpData.forEach((entry) => {
        if (!entry.goodXTicketNo) {
          entry.goodXStatus = 'Private'
          entry.claimResponse = null
          entry.claimStatus = 'N/A'
        }
      })

      // Fetch patient and booking data in parallel
      const [patientRecords] = await Promise.all([
        readPatients({ _id: { $in: Array.from(patientIds) } }, {}, 'firstName lastName idNo')
      ])

      // Create lookup maps for faster access
      const patientMap = new Map(patientRecords.map((p) => [p._id, p]))

      // Apply patient and booking data in a single pass
      tmpData.forEach((entry) => {
        const patient = patientMap.get(entry.userRef)
        if (patient) {
          entry.fullName = `${patient.firstName} ${patient.lastName}`
          entry.idNo = patient.idNo
        }
      })

      setData([...tmpData])
    } catch (e) {
      message.error(handleError(e, true))
    }

    setLoading(false)
  }

  /**
   * Marks invoice as complete
   */
  const markInvoiceAsComplete = async (invoiceId) => {
    try {
      // First get the invoice data to extract billingId
      const invoice = data.find((inv) => inv._id === invoiceId)

      await updateInvoice(invoiceId, { status: 'completed' })

      // Log audit trail for invoice completion
      await logAuditEvent(AuditActions.UPDATE, AuditEntityTypes.INVOICE, invoiceId, 'Invoice marked as completed', {
        invoiceId,
        billingId: invoice?.billingId,
        newStatus: 'completed',
        action: 'status_update'
      })

      message.success('Invoice marked as completed')
      fetchInvoices()
      return true
    } catch (e) {
      message.error(handleError(e, true))
      return false
    }
  }

  const handlePrintInvoice = async (record) => {
    setPrintingInvoiceId(record._id)
    try {
      const entity = store.getState().core.entity
      const invoice = await invoicesAggregate([
        {
          $match: {
            $expr: { $eq: [{ $toString: '$_id' }, record._id] }
          }
        },
        {
          $addFields: {
            userRefObjectId: { $cond: [{ $eq: ['$userRef', null] }, null, { $toObjectId: '$userRef' }] },
            clinicRefObjectId: { $cond: [{ $eq: ['$clinicRef', null] }, null, { $toObjectId: '$clinicRef' }] },
            medicalProfRefObjectId: {
              $cond: [{ $eq: ['$medicalProfRef', null] }, null, { $toObjectId: '$medicalProfRef' }]
            }
          }
        },
        {
          $lookup: {
            from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_admin_clinics`,
            localField: 'clinicRefObjectId',
            foreignField: '_id',
            as: 'clinicRecord'
          }
        },
        {
          $lookup: {
            from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_auth_users`,
            localField: 'userRefObjectId',
            foreignField: '_id',
            as: 'patientRecord'
          }
        },
        {
          $lookup: {
            from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_auth_users`,
            localField: 'medicalProfRefObjectId',
            foreignField: '_id',
            as: 'medicalProfRecord'
          }
        },
        {
          $lookup: {
            from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_billing_records`,
            localField: 'bookingRef',
            foreignField: 'bookingRef',
            as: 'billingRecords'
          }
        },
        {
          $project: {
            patientRecord: {
              profileImage: 0
            },
            medicalProfRecord: {
              profileImage: 0
            },
            clinicRecord: {
              clinicLogo: 0,
              billingMacros: 0
            }
          }
        },
        {
          $unwind: '$clinicRecord'
        },
        {
          $unwind: '$patientRecord'
        },
        {
          $unwind: '$medicalProfRecord'
        }
      ])

      const invoiceHtml = InvoiceTemplate({ ...invoice[0], invoice: record, entity })

      const pdfUrl = await generateCraftMyPdfReport(invoiceHtml, 'b1b77b233fd40c3c')
      window.open(pdfUrl, '_blank')
    } catch (error) {
      console.error('Error printing invoice:', error)
      message.error('Failed to generate invoice PDF')
    } finally {
      setPrintingInvoiceId(null)
    }
  }

  return {
    loading,
    data,
    page,
    pageSize,
    total,
    filters,
    setPage,
    setPageSize,
    setFilters,
    fetchInvoices,
    markInvoiceAsComplete,
    handlePrintInvoice,
    printingInvoiceId,
    setPrintingInvoiceId
  }
}
